import { debounce } from "lodash";
import React, { ComponentType } from "react";
import { connect } from "react-redux";
import { Form, actions } from 'react-redux-form';
import { routerActions } from 'react-router-redux';
import Alert from 'react-s-alert';
import { bindActionCreators } from "redux";
import { NotificationAlert } from "src/components/common";
import { CheckboxFormInput, NumberFormInput, SelectFormInput, TextFormInput } from "src/components/form";
import { RestrictedLayout } from 'src/components/layouts';
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { RequestState } from "src/enumerations/RequestState";
import { renderRegistrationFormSaveBlock, renderRegistrationFormSaveSuccess, renderRegistrationFormSaveWarning } from "src/helpers/alert";
import { HealthDistrictCreateBreadcrumbs, HealthDistrictDetailsBreadcrumbs } from 'src/helpers/breadcrumbHelper';
import { RegistrationComponent, renderFormControls } from "src/helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "src/helpers/lookupHelper";
import { HealthDistrictSubMenu } from 'src/helpers/subMenuHelper';
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITmdState } from "src/reducers";
import { TogglePageItem } from 'src/reducers/pages/togglePage';
import { IRegistrationFormState } from "src/reducers/reactReduxForms";
import { IHealthDistrictReduxFormState, initialHealthDistrictState } from 'src/reducers/reactReduxForms/healthDistrict';
import * as HealthDistrictActions from '../../../actions/healthDistrict';
import * as NavigationActions from '../../../actions/navigation';
import * as ToggleActions from '../../../actions/pages/togglePage';
import * as Dtos from '../../../dtos/Tmd.dtos';
import * as AuthenticationHelper from '../../../helpers/authentication';

import './HealthDistrictPage.scss';

interface IHealthDistrictPageParams {
    healthDistrictId: number;
}

interface IHealthDistrictPageProps extends IRouterProps<IHealthDistrictPageParams> {

    form: Dtos.HealthDistrict & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IHealthDistrictReduxFormState;

    healthDistrictId: number;

    // From state
    healthDistrict: Dtos.HealthDistrict;
    lookups: Dtos.Lookup[];


    loadingHealthDistrict: boolean;
    loadHealthDistrictSuccess: boolean;
    loadHealthDistrictFailure: boolean;

    savingHealthDistrict: boolean;
    saveHealthDistrictSuccess: boolean;
    saveHealthDistrictFailure: boolean;

    creatingHealthDistrict: boolean;
    createHealthDistrictSuccess: boolean;
    createHealthDistrictFailure: boolean;

    validatingHealthDistrict: boolean;

    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    user: Dtos.User;

    toggleItems: TogglePageItem[];
}

interface IHealthDistrictPageActions extends IRouterActions {
    clearHealthDistrict: HealthDistrictActions.IHealthDistrictClearActionCreator;
    loadHealthDistrict: HealthDistrictActions.IHealthDistrictLoadByIdActionCreator;

    saveHealthDistrict: HealthDistrictActions.IHealthDistrictSaveActionCreator;
    createHealthDistrict: HealthDistrictActions.IHealthDistrictCreateActionCreator;
    validateHealthDistrict: HealthDistrictActions.IHealthDistrictValidateActionCreator;

    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

    toggleExpand: ToggleActions.IToggleExpandedStateActionCreator;

}

type HealthDistrictPageProps = IHealthDistrictPageProps & IHealthDistrictPageActions;

const reduxFormName: string = "reduxForms.healthDistrict";

class HealthDistrictPage extends React.PureComponent<HealthDistrictPageProps, any> {
    hasBeenSubmitted: boolean;

    constructor(props: HealthDistrictPageProps) {
        super(props);

        this.state = {
            hasBeenSubmitted: false
        }

        this.clearHealthDistrict = this.clearHealthDistrict.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.loadForm = this.loadForm.bind(this);
        this.debounceValidate = this.debounceValidate.bind(this);
    }

    componentDidMount() {
        const {
            permissions,
            loadHealthDistrict,
            healthDistrictId,
            createHealthDistrict,
            setBreadcrumbs,
            setDrawerItems
        } = this.props;
        

        if (healthDistrictId) {
            setDrawerItems(HealthDistrictSubMenu(healthDistrictId, permissions));
            setBreadcrumbs(HealthDistrictDetailsBreadcrumbs(healthDistrictId));

            loadHealthDistrict(healthDistrictId);

        } else if (!healthDistrictId) {
            createHealthDistrict();
            setDrawerItems([]);
            setBreadcrumbs(HealthDistrictCreateBreadcrumbs());
        }
    }

    componentDidUpdate(prevProps: HealthDistrictPageProps, _prevState) {
        const {
            healthDistrict,
            healthDistrictId,
            form,
            navigate,
            loadHealthDistrict,
            loadingHealthDistrict,
            loadHealthDistrictSuccess,
            createHealthDistrict,
            creatingHealthDistrict,
            createHealthDistrictSuccess,
            savingHealthDistrict,
            saveHealthDistrictSuccess,
            validationFailures,
            setBreadcrumbs,
            setDrawerItems,
            permissions
        } = this.props;
        
        if (healthDistrictId){
            setDrawerItems(HealthDistrictSubMenu(healthDistrictId, permissions));
            if (healthDistrict?.name) {
                setBreadcrumbs(HealthDistrictDetailsBreadcrumbs(healthDistrictId, healthDistrict.name));
            }
        }

        if (healthDistrictId && prevProps.healthDistrictId != healthDistrictId) {
            loadHealthDistrict(healthDistrictId);
        }
        else if (!healthDistrictId && prevProps.healthDistrictId) {
            createHealthDistrict();
        }

        if (!loadingHealthDistrict && prevProps.loadingHealthDistrict) {
            if (loadHealthDistrictSuccess) {
                this.loadForm();
            }
        }

        if (form != prevProps.form) {
            this.debounceValidate();
        }

        if (!savingHealthDistrict && prevProps.savingHealthDistrict) {
            if (saveHealthDistrictSuccess) {
                if (validationFailures && validationFailures.length > 0) {

                    Alert.warning(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveWarning(validationFailures)}
                            icon={FontAwesomeIcons.Solid.EXCLAMATION_CIRCLE}
                        />
                    );

                } else {

                    Alert.success(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveSuccess()}
                            icon={FontAwesomeIcons.Solid.CHECK}
                        />
                    );

                }
                if (form.saveAndReturn) {
                    navigate("/settings/healthdistricts")
                } else if (!form.saveAndReturn && healthDistrictId && healthDistrictId > 0) {
                    this.loadForm();
                } else if (!form.saveAndReturn && !healthDistrictId && healthDistrict && healthDistrict.id > 0) {
                    navigate("/settings/healthdistricts/" + healthDistrict.id + "/details");
                }
            }
            else {

                Alert.error(
                    <NotificationAlert
                        alertContent={this.renderErrorAlert()}
                        icon={FontAwesomeIcons.Solid.TIMES_OCTAGON}
                    />
                );

            }
        }

        if (!creatingHealthDistrict && prevProps.creatingHealthDistrict) {
            if (createHealthDistrictSuccess) {
                this.loadForm();
            }
        }
    }

    componentWillUnmount() {
        const {
            resetForm,
            loadForm
        } = this.props

        this.clearHealthDistrict();
        loadForm(reduxFormName, initialHealthDistrictState);
        resetForm(reduxFormName);
    }

    clearHealthDistrict() {
        this.props.clearHealthDistrict();
    }

    showHealthDistrict(): boolean {
        const {
            loadingHealthDistrict,
            loadHealthDistrictSuccess
        } = this.props

        if (!loadingHealthDistrict && loadHealthDistrictSuccess) {
            return true;
        }

        return false;
    }

    debounceValidate = debounce(async () => {
        const hasBeenSubmitted = this.state?.hasBeenSubmitted;
        const {
            form,
            validatingHealthDistrict,
            validateHealthDistrict
        } = this.props;

        let changes: Dtos.HealthDistrict = {} as Dtos.HealthDistrict;

        if (form && hasBeenSubmitted && !validatingHealthDistrict) {
            await validateHealthDistrict(Object.assign({ ...form }, changes));
        }
    }, 250);

    render() {
        const {
            loadingHealthDistrict
        } = this.props

        return (
            <RestrictedLayout
                loading={loadingHealthDistrict} 
                permission={ Dtos.Permission.HealthDistrictView }
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const {
            form,
            healthDistrictId,
            lookups,
            validationFailures,
            formProperties,
            formState,
            changeForm,
            permissions,
            savingHealthDistrict
        } = this.props;

        const hasPermission: boolean = AuthenticationHelper.hasCreateEditPermissionFor(healthDistrictId, undefined, permissions, Dtos.Permission.HealthDistrictUpdate, Dtos.Permission.HealthDistrictCreate);
        const formDisabled: boolean = !hasPermission || savingHealthDistrict || (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            details: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-6">
                                    {
                                        renderFormControls(
                                            form,
                                            "healthDistrict",
                                            {
                                                name: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-2",
                                                    fieldClassName: "col-sm-9 generic-form-field",
                                                }
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )
                                    }
                                </div>
                                <div className="col-md-6">
                                    {
                                        renderFormControls(
                                            form,
                                            "healthDistrict",
                                            {
                                                stateId: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: SelectFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        options: generateOptionsFromLookup(findLookup("HealthDistrict", "StateId", lookups), form.stateId)
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-2",
                                                    fieldClassName: "col-sm-10 generic-form-field",
                                                    inputLabel: "State"
                                                }
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )
                                    }
                                </div>
                            </div>
                            <div className="form-row pt-1 pb-4 px-4">
                                <div className="col-md-6">
                                    {
                                        renderFormControls(
                                            form,
                                            "healthDistrict",
                                            {
                                                active: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: CheckboxFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        label: "Active"
                                                    },
                                                    inputLabel: " ",
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-2",
                                                    fieldClassName: "col-sm-10 generic-form-field d-flex align-items-center pt-2",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )
                                    }
                                </div>
                                <div className="col-md-6">
                                    {
                                        renderFormControls(
                                            form,
                                            "healthDistrict",
                                            {
                                                rank: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: NumberFormInput,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-2",
                                                    fieldClassName: "col-sm-10 generic-form-field",
                                                }
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    </>
                ),
            }
        };

        return (
            <Form model={reduxFormName}
                onSubmit={(val, _event) => this.handleOnSubmit(val)}
                onSubmitFailed={(val) => this.handleOnSubmitFailed(val)}
            >
                {
                    renderFormControls(form, "healthDistrict", formComponents, lookups, validationFailures, formProperties, changeForm)
                }
                <div className="mt-4 mb-4 text-right">
                    <button disabled={formDisabled}
                        onClick={
                            () => {
                                this.props.changeForm(reduxFormName + ".saveAndReturn", false);
                            }
                        }
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>

                    <button disabled={formDisabled}
                        onClick={() => {
                            this.props.changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={this.onCancel} className="btn btn-primary mr-2" > Cancel </button>
                </div>
            </Form>
        );
    }


    renderErrorAlert(): any {

        return (
            <div>
                The form was <strong> not </strong> saved.
            </div>
        );

    }

    renderWarningAlert(): any {
        const {
            validationFailures,
        } = this.props

        return (
            <div>
                The form was saved but was invalid with {validationFailures.length} errors.
            </div>
        );

    }

    renderSaveSuccessAlert(): any {
        return (
            <div>The form has been saved successfully</div>
        );
    }

    handleOnSubmit(data: Dtos.HealthDistrict) {
        const { hasBeenSubmitted } = this.state;
        const {
            saveHealthDistrict
        } = this.props;

        if (!hasBeenSubmitted) {
            this.setState({ hasBeenSubmitted: true });
        }

        if (data) {
            let changes: Dtos.HealthDistrict = {} as Dtos.HealthDistrict;
            saveHealthDistrict(Object.assign({ ...data }, changes));
        }
    }


    handleOnSubmitFailed(_data: Dtos.HealthDistrict) {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    }


    onCancel() {
        const {
            navigate
        } = this.props;

        navigate("/settings/healthdistricts");
    }

    loadForm() {
        const {
            loadForm,
            healthDistrict,
        } = this.props

        let extraData: Dtos.HealthDistrict = {} as Dtos.HealthDistrict;

        loadForm(reduxFormName, Object.assign({ ...healthDistrict }, extraData));
    }
}


const mapStateToProps = (state: ITmdState, ownProps: HealthDistrictPageProps): IHealthDistrictPageProps => {

    let healthDistrict: Dtos.HealthDistrict | undefined = !(state.healthDistricts.formData instanceof Array) ? state.healthDistricts.formData : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {

        match: ownProps.match,
        healthDistrictId: ownProps.match && ownProps.match.params.healthDistrictId && ownProps.match.params.healthDistrictId.toString() != "create" ? ownProps.match.params.healthDistrictId : undefined!,

        history: ownProps.history,
        location: ownProps.location,

        healthDistrict: healthDistrict!,

        loadingHealthDistrict: state.healthDistricts.loadState && state.healthDistricts.loadState.status == RequestState.Pending,
        loadHealthDistrictSuccess: state.healthDistricts.loadState && state.healthDistricts.loadState.status == RequestState.Success,
        loadHealthDistrictFailure: state.healthDistricts.loadState && state.healthDistricts.loadState.status == RequestState.Failure,

        savingHealthDistrict: state.healthDistricts.saveState && state.healthDistricts.saveState.status == RequestState.Pending,
        saveHealthDistrictSuccess: state.healthDistricts.saveState && state.healthDistricts.saveState.status == RequestState.Success,
        saveHealthDistrictFailure: state.healthDistricts.saveState && state.healthDistricts.saveState.status == RequestState.Failure,

        creatingHealthDistrict: state.healthDistricts.createState && state.healthDistricts.createState.status == RequestState.Pending,
        createHealthDistrictSuccess: state.healthDistricts.createState && state.healthDistricts.createState.status == RequestState.Success,
        createHealthDistrictFailure: state.healthDistricts.createState && state.healthDistricts.createState.status == RequestState.Failure,

        validatingHealthDistrict: state.healthDistricts.validateState && state.healthDistricts.validateState.status == RequestState.Pending,

        user: state.user.data,
        permissions: permissions,
        lookups: state.healthDistricts.lookups,

        form: state.reduxForms.healthDistrict,
        formState: state.healthDistricts.formState,
        formProperties: state.healthDistricts.formProperties,

        reduxFormState: state.reduxForms.formState.healthDistrict,
        validationFailures: healthDistrict ?
            state.healthDistricts.validationFailures :
            undefined,

        toggleItems: state.togglePage.items,
    };
};

const mapDispatchToProps = (dispatch, ownProps: HealthDistrictPageProps): IHealthDistrictPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadHealthDistrict: bindActionCreators(HealthDistrictActions.LoadHealthDistrictById, dispatch),
        clearHealthDistrict: bindActionCreators(HealthDistrictActions.Clear, dispatch),

        saveHealthDistrict: bindActionCreators(HealthDistrictActions.SaveHealthDistrict, dispatch),
        createHealthDistrict: bindActionCreators(HealthDistrictActions.CreateHealthDistrict, dispatch),
        validateHealthDistrict: bindActionCreators(HealthDistrictActions.ValidateHealthDistrict, dispatch),

        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),

        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),

        toggleExpand: bindActionCreators(ToggleActions.ToggleExpanded, dispatch)
    };
};

export default
    RouterHoc<IHealthDistrictPageParams, HealthDistrictPageProps>((connect(mapStateToProps, mapDispatchToProps)(HealthDistrictPage) as unknown as ComponentType<HealthDistrictPageProps>)) as ComponentType<any>;